/**
 * @file ShopCarrierSettingsPage.tsx
 * @description Страница настройки перевозчика (phase19.12).
 *
 * Раньше настройки жили в модальном окне, но после добавления параметров
 * расчёта и диагностики форма перестала помещаться — работать в модалке стало
 * неудобно (решение пользователя 2026-08-13). Теперь это полноценная страница
 * с тремя разделами: «Основное», «Расчёт доставки», «Проверка».
 */

import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { CarrierDeliveryFields } from "../../../components/carriers/CarrierDeliveryFields";
import { CarrierDiagnosticsPanel } from "../../../components/carriers/CarrierDiagnosticsPanel";
import { CarrierQuoteCalculator } from "../../../components/carriers/CarrierQuoteCalculator";
import { CarrierMainFields } from "../../../components/carriers/CarrierMainFields";
import { CarrierWebhookPanel } from "../../../components/carriers/CarrierWebhookPanel";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  listShopCarriers,
  upsertShopCarrier,
  type CarrierKind,
  type ShopCarrierConfigDto,
} from "../../../lib/api-shop-carriers";
import {
  carrierConfigToForm,
  carrierFormToInput,
  type CarrierFormState,
} from "../../../lib/carrier-settings-form";

const TABS = [
  { key: "main", label: "Основное" },
  { key: "delivery", label: "Расчёт доставки" },
  { key: "check", label: "Проверка" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function ShopCarrierSettingsPage() {
  const { carrier: carrierParam, slug } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const carrier = (carrierParam ?? "SDEK").toUpperCase() as CarrierKind;
  const backHref = `/shop/${slug}/carriers`;

  const [config, setConfig] = useState<ShopCarrierConfigDto | null>(null);
  const [form, setForm] = useState<CarrierFormState | null>(null);
  const [tab, setTab] = useState<TabKey>("main");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Растёт после правки подписки — сигнал диагностике перепроверить себя. */
  const [checkToken, setCheckToken] = useState(0);

  useEffect(() => {
    let alive = true;
    void (async () => {
      setLoading(true);
      try {
        const list = await listShopCarriers();
        const found = list.find((c) => c.carrier === carrier) ?? null;
        if (!alive) return;
        setConfig(found);
        setForm(found ? carrierConfigToForm(found) : null);
        setError(found ? null : "Перевозчик не найден");
      } catch (err) {
        if (alive) setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [carrier]);

  /**
   * Перечитать конфиг после правки подписки на вебхук.
   *
   * Форму намеренно не пересобираем: менеджер мог оставить несохранённые
   * правки на других вкладках, а состояние подписки живёт вне формы.
   */
  const reloadConfig = useCallback(async (): Promise<void> => {
    try {
      const list = await listShopCarriers();
      const found = list.find((c) => c.carrier === carrier) ?? null;
      if (found) setConfig(found);
    } catch {
      // Индикатор обновится после ручной проверки — ронять экран незачем.
    }
    setCheckToken((v) => v + 1);
  }, [carrier]);

  async function onSave(): Promise<void> {
    if (!form) return;
    const prepared = carrierFormToInput(form);
    if ("error" in prepared) {
      notify.error(prepared.error);
      return;
    }
    setSaving(true);
    try {
      const updated = await upsertShopCarrier(carrier, prepared.input);
      setConfig(updated);
      setForm(carrierConfigToForm(updated));
      notify.success("Настройки сохранены");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-on-surface-variant">
        <Loader2 className="size-4 animate-spin" /> Загружаем настройки…
      </div>
    );
  }

  if (error || !config || !form) {
    return (
      <div className="flex flex-col gap-4 p-1">
        <Alert severity="error">{error ?? "Перевозчик не найден"}</Alert>
        <Button variant="outline" onClick={() => navigate(backHref)}>
          <ArrowLeft className="mr-1.5 size-4" /> К списку перевозчиков
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to={backHref}
          className="inline-flex min-h-11 items-center gap-1.5 text-sm text-on-surface-variant hover:text-on-surface"
        >
          <ArrowLeft className="size-4" /> Транспортные компании
        </Link>
        <div className="label-editorial">Доставка · Настройка</div>
        <h1 className="text-2xl font-semibold">{config.carrierLabel}</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Параметры использования в вашем магазине. Ключи доступа к API настраивает
          платформенный администратор в разделе «Интеграции».
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Разделы настройки"
        className="flex shrink-0 gap-1.5 overflow-x-auto"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`h-9 shrink-0 rounded-full px-4 text-sm transition-colors ${
              tab === t.key
                ? "bg-primary font-medium text-on-primary"
                : "bg-surface-low text-on-surface-variant hover:bg-accent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <EditorialCard>
        <EditorialCard.Body>
          {tab === "main" && (
            <CarrierMainFields
              value={form}
              onChange={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))}
              carrier={carrier}
              pickupPointCount={config.pickupPointCount}
              lastPickupSync={config.lastPickupSync}
            />
          )}
          {tab === "delivery" && (
            <div className="flex flex-col gap-8">
              <CarrierDeliveryFields
                value={form}
                onChange={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))}
                carrier={carrier}
              />
              {/* Справочный расчёт: проверить настройки, не открывая заказ. */}
              <div className="border-t border-outline-variant pt-6">
                <CarrierQuoteCalculator
                  carrier={carrier}
                  carrierSettingsHref={`${backHref}/${carrier}/settings`}
                />
              </div>
            </div>
          )}
          {tab === "check" && (
            <div className="flex flex-col gap-8">
              <CarrierWebhookPanel
                carrier={carrier}
                subscriptionUuid={config.webhookSubscriptionUuid}
                disabled={!config.configured}
                onChanged={reloadConfig}
              />
              <div className="border-t border-outline-variant pt-6">
                <CarrierDiagnosticsPanel
                  carrier={carrier}
                  disabled={!config.configured}
                  rerunToken={checkToken}
                />
              </div>
            </div>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {/* Сохранение доступно с любой вкладки — форма одна. */}
      {tab !== "check" && (
        <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-surface-lowest/95 py-3 backdrop-blur">
          <Button variant="ghost" onClick={() => navigate(backHref)} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSave()} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-1.5 size-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 size-4" />
            )}
            Сохранить
          </Button>
        </div>
      )}
    </div>
  );
}
